import { FC, ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';

/**
 * Pusty placeholder dla slotu obrazka (logo / okładka). Animowana ilustracja
 * line-art w tej samej stylistyce co puste stany tabel (`TableEmptyState`):
 * stonowany szary `currentColor` + akcent marki (teal), miękki obłok, migoczące
 * znaczki. Reaguje na motyw i `prefers-reduced-motion`.
 */

const ACCENT = '#0a7465';

const floaty = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.2; transform: scale(0.85); }
  50% { opacity: 0.85; transform: scale(1.05); }
`;

const blobDrift = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(5px); }
`;

const reduceMotion = css`
  @media (prefers-reduced-motion: reduce) {
    * { animation: none !important; }
  }
`;

export type ImagePlaceholderVariant = 'logo' | 'cover';

const Box = styled.div<{ $variant: ImagePlaceholderVariant }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  width: 100%;
  padding: 22px 20px;
  border: 1.5px dashed ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#d1d5db')};
  border-radius: 12px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa')};
  transition: border-color 0.15s ease, background 0.15s ease;

  ${({ $variant }) => ($variant === 'cover'
    ? css`min-height: 184px;`
    : css`min-height: 168px; max-width: 280px;`)}

  &:hover {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#9ca3af')};
  }
`;

const IllustrationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
`;

const Title = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 320px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
`;

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const Svg = styled.svg<{ $height: number }>`
  display: block;
  width: auto;
  height: ${({ $height }) => $height}px;

  .blob {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${blobDrift} 9s ease-in-out infinite;
  }
  .sun {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4s ease-in-out infinite;
  }
  .spark {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${twinkle} 3s ease-in-out infinite;
  }
  .spark-2 { animation-delay: 1.1s; }

  ${reduceMotion}
`;

// Animowana ilustracja „obrazka" — ramka ze słońcem i górami (klasyczny
// placeholder), słońce delikatnie unosi się, znaczki migoczą. Eksportowana
// samodzielnie, by dało się ją wstawić w gotowe sloty (np. logo / cover karty).
export const ImagePlaceholderArt: FC<{ height?: number }> = ({ height = 78 }) => (
  <Svg viewBox="0 0 170 120" $height={height} role="img" aria-hidden="true">
    <ellipse className="blob" cx={88} cy={66} rx={56} ry={30} fill="currentColor" opacity={0.05} />

    {/* ramka obrazka */}
    <rect {...stroke} x={36} y={28} width={98} height={70} rx={8} fill="currentColor" fillOpacity={0.04} />

    {/* słońce (akcent) */}
    <circle className="sun" cx={62} cy={50} r={9} fill={ACCENT} fillOpacity={0.18} stroke={ACCENT} strokeWidth={2} />

    {/* góry */}
    <g {...stroke} opacity={0.7}>
      <path d="M40 90 L66 60 L86 84" />
      <path d="M76 90 L100 64 L124 92 L130 92" />
    </g>
    {/* podstawa ramki */}
    <path {...stroke} d="M40 90 H130" opacity={0.5} />

    {/* migoczące znaczki */}
    <g {...stroke}>
      <path
        className="spark"
        d="M140 30 l1.8 4.2 4.2 1.8 -4.2 1.8 -1.8 4.2 -1.8 -4.2 -4.2 -1.8 4.2 -1.8 Z"
        fill={ACCENT}
        stroke={ACCENT}
        opacity={0.7}
      />
      <path className="spark spark-2" d="M30 40 v7 M26.5 43.5 h7" opacity={0.5} />
    </g>
  </Svg>
);

interface ImagePlaceholderProps {
  /** `logo` (kompaktowy, kwadratowy) lub `cover` (szeroki). */
  variant?: ImagePlaceholderVariant;
  title: string;
  description?: string;
  /** Akcja (np. przycisk „Prześlij plik"). */
  action?: ReactNode;
  className?: string;
}

export const ImagePlaceholder: FC<ImagePlaceholderProps> = ({
  variant = 'cover',
  title,
  description,
  action,
  className,
}) => (
  <Box $variant={variant} className={className}>
    <IllustrationWrap>
      <ImagePlaceholderArt height={78} />
    </IllustrationWrap>
    <Title>{title}</Title>
    {description && <Description>{description}</Description>}
    {action && <Actions>{action}</Actions>}
  </Box>
);
